import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FeedbackError,
  FilterChips,
  Status,
  formatHandoverSchedule,
} from '@/components';
import useDashboardResource from '@/hooks/useDashboardResource';
import useFeatureFlag from '@/hooks/useFeatureFlag';
import { Column } from '@/types/common';
import {
  ARREARS_BUCKET_LABEL,
  ARREARS_BUCKET_ORDER,
  ARREARS_BUCKET_STAT_KEY,
  ArrearsBucket,
  EscalationStatus,
  GetRentalDashboardSummaryParams,
  HandoverStatus,
  RentalDashboardQueues,
  RentalListItem,
  RentalStats,
} from '@/types/rental';
import {
  getRentalDashboardQueues,
  getRentalDashboardSummary,
  getRentalStats,
  getRentals,
} from '@/services/api/rentals';
import { formatDateToApi, toApiDateRange } from '@/utils';
import { formatRupiah } from '@/utils/format';
import {
  formatArrears,
  formatVehicleLabel,
  refundStatusLabel,
  refundToneClass,
  showsEscalationPill,
} from '@/pages/rent-activity/rentalDisplay';
import ActionQueueSection from './components/ActionQueueSection';
import FleetCards from './components/FleetCards';
import PerformaCards from './components/PerformaCards';
import QueueStat from './components/QueueStat';
import { formatOverdueBy, isPastDue } from './dashboardDisplay';

/**
 * Kill switch for the rolling money window, flipped from Remote Config rather
 * than by a deploy. Production deploys here are manual and the tag trigger is
 * broken, so a revert costs a release while a flag costs a config edit — and
 * this is the one change that moves a number ops reads every morning.
 *
 * Default false, so the new window is what ships. Turning it ON restores the
 * old single-day behaviour.
 */
const LEGACY_MONEY_WINDOW_FLAG = 'web_rent_dashboard_legacy_money_window';

const mitraColumn: Column<RentalListItem> = {
  key: 'driver',
  title: 'Mitra',
  render: (_value: unknown, record: RentalListItem) => (
    <div className="leading-tight">
      <div className="text-[14px] text-gray-900">
        {record.driver?.name || '-'}
      </div>
      <div className="text-[12px] text-gray-500">
        {record.driver?.code || '-'}
      </div>
    </div>
  ),
};

const unitColumn: Column<RentalListItem> = {
  key: 'vehicle',
  title: 'Unit',
  render: (_value: unknown, record: RentalListItem) =>
    record.vehicle ? (
      <div className="leading-tight">
        <div className="text-[14px] text-gray-900">
          {record.vehicle.platNomor || '-'}
        </div>
        <div className="text-[12px] text-gray-500">
          {formatVehicleLabel(record.vehicle) || '-'}
        </div>
      </div>
    ) : (
      // vehicle is null until a unit is assigned (PRE_ORDER).
      <span className="text-[12px] text-gray-400">Belum ditugaskan</span>
    ),
};

const packageColumn: Column<RentalListItem> = {
  key: 'package',
  title: 'Package',
  render: (_value: unknown, record: RentalListItem) =>
    record.package?.name ? (
      <Status value={record.package.name} type="packageScheme" size="small" />
    ) : (
      <span className="text-gray-400">-</span>
    ),
};

const escalationColumn: Column<RentalListItem> = {
  key: 'escalationStatus',
  title: 'Eskalasi',
  render: (_value: unknown, record: RentalListItem) =>
    showsEscalationPill(record.escalationStatus) ? (
      <Status
        value={record.escalationStatus}
        type="escalationStatus"
        size="medium"
      />
    ) : (
      <span className="text-gray-400">-</span>
    ),
};

const arrearsColumn: Column<RentalListItem> = {
  key: 'arrears',
  title: 'Tunggakan',
  render: (_value: unknown, record: RentalListItem) => {
    const label = formatArrears(record.arrears);
    return label ? (
      <span className="text-[14px] font-medium text-gray-900">{label}</span>
    ) : (
      <span className="text-gray-400">-</span>
    );
  },
};

/**
 * The schedule column, with the late ones saying so.
 *
 * A pill and a sign, not a colour: the queue now reaches 30 days back, so most
 * screens will carry a mix, and colour alone excludes anyone who cannot see it.
 */
const scheduleColumn = (
  title: string,
  dateOf: (record: RentalListItem) => string | null | undefined,
  today: string,
  withSlot = false
): Column<RentalListItem> => ({
  key: 'schedule',
  title,
  render: (_value: unknown, record: RentalListItem) => {
    const date = dateOf(record);
    const late = isPastDue(date, today);
    return (
      <div className="leading-tight">
        <span className="text-[14px] text-gray-900">
          {withSlot
            ? formatHandoverSchedule(record.slot?.date ?? date, {
                startTime: record.slot?.startTime,
                endTime: record.slot?.endTime,
              })
            : formatHandoverSchedule(date)}
        </span>
        {late && date && (
          <div className="mt-1">
            <span className="inline-block rounded bg-red-100 px-2 py-0.5 text-[12px] font-medium text-red-700">
              {formatOverdueBy(date, today)}
            </span>
          </div>
        )}
      </div>
    );
  },
});

type QueueKey =
  'handoverToday' | 'returnToday' | 'overdue' | 'escalation' | 'refundFailed';

/**
 * Tab order, and the only list of queues there is: the labels and the bodies
 * come from one registry below, so a queue cannot exist in the bar and be
 * missing from the screen.
 *
 * "Pembayaran Hari Ini" used to lead this list. It was removed, not hidden: the
 * Pembayaran menu is a strict superset of it (same three figures, plus filters
 * and search), and a dashboard of action queues opening on a list that needs no
 * action was the one tab not earning its place.
 */
const QUEUE_ORDER: QueueKey[] = [
  'handoverToday',
  'returnToday',
  'overdue',
  'escalation',
  'refundFailed',
];

type QueueCounts = RentalDashboardQueues['queues'];

/**
 * What the queue fetchers return before the server's window has landed. A
 * constant, so it never changes a callback's identity and never re-triggers a
 * fetch.
 */
const EMPTY_QUEUE_PAGE = {
  items: [] as RentalListItem[],
  pagination: { size: 0, page: 1, lastPage: 1, total: 0 },
};

interface QueueView {
  label: string;
  /**
   * Takes the counts rather than reading them from a nullable in scope. That is
   * what lets the bodies below be written without `?? 0` fallbacks: the strip
   * is rendered only once the counts have loaded, and the signature says so, so
   * nobody can move the call out of that guard without the compiler objecting.
   */
  stats: (counts: QueueCounts) => React.ReactNode;
  section: () => React.ReactNode;
}

/** The stat line above a queue. */
const QueueStats: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="mb-3 flex flex-wrap items-center gap-x-6 gap-y-1">
    {children}
  </div>
);

const RentDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const useLegacyMoneyWindow = useFeatureFlag(
    LEGACY_MONEY_WINDOW_FLAG,
    'boolean'
  );

  // Frozen for the session on purpose: the queues re-fetch on paging, and a
  // "today" that rolled over mid-scroll would silently change the question.
  const today = useMemo(() => formatDateToApi(new Date()), []);

  const [overdueBucket, setOverdueBucket] = useState<ArrearsBucket | null>(
    null
  );
  const [activeQueue, setActiveQueue] = useState<QueueKey>('handoverToday');

  // The window ops picked, or null while they are still on the default.
  //
  // The default is DERIVED, never captured into state. useFeatureFlag returns
  // false on the first render and only publishes the real Remote Config value
  // from an effect after fetchAndActivate resolves — so reading it once in a
  // useState initializer would freeze the flag at false and the kill switch
  // could never fire. Which would defeat its entire purpose here: production
  // deploys are manual, and the flag exists so the window can be reverted
  // without one.
  const [pickedRange, setPickedRange] =
    useState<GetRentalDashboardSummaryParams | null>(null);

  // Sending NO bounds is the default, and that is deliberate: the backend owns
  // the window (30 days ending yesterday) and echoes back what it applied.
  // Computing the same window here would put one constant in two repositories —
  // the very duplication the queue window below is written to avoid — and the
  // two would drift the first time either side moved it.
  //
  // Yesterday, not today, because a charge only becomes due once it passes its
  // own rental's boundary, derived from that mitra's handover time-of-day: today
  // is matured for some rentals and not for others.
  const summaryRange = useMemo(
    () =>
      pickedRange ??
      // The kill switch needs an explicit window to switch TO, so this one leg
      // does name its dates. It is the old behaviour, frozen.
      (useLegacyMoneyWindow ? { startDate: today, endDate: today } : {}),
    [pickedRange, useLegacyMoneyWindow, today]
  );

  const fetchSummary = useCallback(
    ({ signal }: { signal: AbortSignal }) =>
      getRentalDashboardSummary(summaryRange, { signal }),
    [summaryRange]
  );
  const { resource: summary, reload: reloadSummary } =
    useDashboardResource(fetchSummary);

  // Takes no window at all, so it stays out of the effect above: moving the
  // date filter used to re-fetch these four counts for nothing.
  const fetchStats = useCallback(
    ({ signal }: { signal: AbortSignal }) => getRentalStats({}, { signal }),
    []
  );
  const { resource: stats, reload: reloadStats } =
    useDashboardResource(fetchStats);

  // The window for the scheduled queues is the SERVER's, not ours. Asking with
  // no bounds and reading back what it used is what keeps the stat strip and
  // the list beneath it describing the same days — recomputing it here would
  // put one constant in two repositories.
  const fetchQueues = useCallback(
    ({ signal }: { signal: AbortSignal }) =>
      getRentalDashboardQueues({}, { signal }),
    []
  );
  const { resource: queues, reload: reloadQueues } =
    useDashboardResource(fetchQueues);

  // Memoised on the two strings, not rebuilt per render. Without this the
  // queue fetchers below get a fresh identity on every render, and
  // ActionQueueSection re-fetches on every one of them — its effect depends on
  // fetchPage, and its own setState triggers the next render.
  const queueStart = queues.data?.startDate ?? null;
  const queueEnd = queues.data?.endDate ?? null;
  const queueWindow = useMemo(
    () =>
      queueStart && queueEnd
        ? { startDate: queueStart, endDate: queueEnd }
        : null,
    [queueStart, queueEnd]
  );

  const openTab = useCallback(
    (tab: keyof RentalStats) => navigate(`/rent-activity?tab=${tab}`),
    [navigate]
  );

  const openRental = useCallback(
    (record: RentalListItem) => {
      if (record.id) navigate(`/rent-activity/${record.id}`);
    },
    [navigate]
  );

  // One getRentals call per queue. That also satisfies the API's constraint
  // that the scheduledHandover* and scheduledReturn* pairs never travel in the
  // same request (the server 422s on the combination).
  const fetchHandovers = useCallback(
    async (page: number, size: number) => {
      if (!queueWindow) return EMPTY_QUEUE_PAGE;
      const result = await getRentals({
        handoverStatus: [HandoverStatus.WAITING_HANDOVER],
        scheduledHandoverStartDate: queueWindow.startDate,
        scheduledHandoverEndDate: queueWindow.endDate,
        page,
        size,
      });
      // Returned in the server's order, deliberately unsorted here.
      //
      // Sorting past-due rows to the top client-side only reorders the page
      // that came back, so a late row on page 3 stays on page 3 while page 1
      // looks like it has none. That is worse than no sorting: it reads as a
      // promise the list cannot keep. Real ordering needs a schedule sort on
      // GET rental — `sort` currently accepts only 'arrears' — and is tracked
      // on the backend PR. Until then the "Terlambat N hari" pill is what makes
      // a late row identifiable wherever it sits.
      return result;
    },
    [queueWindow]
  );

  const fetchReturns = useCallback(
    async (page: number, size: number) => {
      if (!queueWindow) return EMPTY_QUEUE_PAGE;
      const result = await getRentals({
        handoverStatus: [HandoverStatus.WAITING_RETURN],
        scheduledReturnStartDate: queueWindow.startDate,
        scheduledReturnEndDate: queueWindow.endDate,
        page,
        size,
      });
      // Server order, same reason as the handover queue above.
      return result;
    },
    [queueWindow]
  );

  const fetchLargestOverdue = useCallback(
    (page: number, size: number) =>
      getRentals({
        isOverdue: true,
        sort: 'arrears',
        ...(overdueBucket ? { arrearsBucket: overdueBucket } : {}),
        page,
        size,
      }),
    [overdueBucket]
  );

  const fetchStuckRefunds = useCallback(
    (page: number, size: number) =>
      getRentals({ refundStuck: true, page, size }),
    []
  );

  const fetchPendingEscalations = useCallback(
    (page: number, size: number) =>
      getRentals({
        escalationStatus: [
          EscalationStatus.REPO_REQUESTED,
          EscalationStatus.SHUTDOWN_PENDING,
        ],
        page,
        size,
      }),
    []
  );

  const windowLabel = queueWindow
    ? `${queueWindow.startDate} s.d. ${queueWindow.endDate}`
    : '…';

  const queueViews: Record<QueueKey, QueueView> = {
    handoverToday: {
      label: 'Serah Terima',
      stats: (counts) => (
        <QueueStats>
          <QueueStat
            label={`Serah terima terjadwal ${windowLabel}`}
            value={`${counts.handoverToday.count} unit`}
          />
        </QueueStats>
      ),
      section: () => (
        <ActionQueueSection<RentalListItem>
          key={`handover-${queueWindow?.startDate ?? 'pending'}`}
          columns={[
            mitraColumn,
            unitColumn,
            scheduleColumn(
              'Jadwal Serah Terima',
              (row) => row.slot?.date ?? null,
              today,
              true
            ),
            packageColumn,
          ]}
          fetchPage={fetchHandovers}
          emptyText="Tidak ada serah terima terjadwal di rentang ini."
          onRowClick={openRental}
        />
      ),
    },
    returnToday: {
      label: 'Pengembalian',
      stats: (counts) => (
        <QueueStats>
          <QueueStat
            label={`Pengembalian terjadwal ${windowLabel}`}
            value={`${counts.returnToday.count} unit`}
          />
        </QueueStats>
      ),
      section: () => (
        <ActionQueueSection<RentalListItem>
          key={`return-${queueWindow?.startDate ?? 'pending'}`}
          columns={[
            mitraColumn,
            unitColumn,
            scheduleColumn(
              'Jadwal Pengembalian',
              (row) => row.scheduledReturnDate,
              today
            ),
            packageColumn,
          ]}
          fetchPage={fetchReturns}
          // Empty because nothing is scheduled, not because loading failed —
          // the return flow does not currently write WAITING_RETURN at all, so
          // this queue stays empty until it does.
          emptyText="Belum ada pengembalian terjadwal di rentang ini."
          onRowClick={openRental}
        />
      ),
    },
    overdue: {
      label: 'Overdue',
      stats: (counts) => (
        <>
          <QueueStats>
            <QueueStat
              label="Sewa overdue"
              value={`${counts.overdue.count} sewa`}
            />
            <QueueStat
              label="Total tunggakan"
              value={formatRupiah(counts.overdue.totalAmount)}
            />
          </QueueStats>
          {/* Triage by how far behind: the counts come from the same badge
              endpoint the chips filter by, so a chip lists what it counted. */}
          <FilterChips<ArrearsBucket>
            className="mb-3"
            ariaLabel="Filter umur tunggakan"
            value={overdueBucket}
            onChange={setOverdueBucket}
            options={ARREARS_BUCKET_ORDER.map((bucket) => ({
              value: bucket,
              label: ARREARS_BUCKET_LABEL[bucket],
              count: stats.data?.[ARREARS_BUCKET_STAT_KEY[bucket]],
            }))}
          />
        </>
      ),
      section: () => (
        <ActionQueueSection<RentalListItem>
          key={`overdue-${overdueBucket ?? 'all'}`}
          columns={[mitraColumn, unitColumn, arrearsColumn, escalationColumn]}
          fetchPage={fetchLargestOverdue}
          emptyText={
            overdueBucket
              ? 'Tidak ada sewa overdue di rentang hari ini.'
              : 'Tidak ada sewa overdue.'
          }
          onRowClick={openRental}
        />
      ),
    },
    escalation: {
      label: 'Eskalasi Menunggu Ops',
      stats: (counts) => (
        <QueueStats>
          <QueueStat
            label="Menunggu ops"
            value={`${
              counts.escalation.shutdownPending +
              counts.escalation.repoRequested
            } sewa`}
          />
          {/* Split because the action differs: one waits for a manual
              shutdown, the other for a repossession run. */}
          <QueueStat
            label="Shutdown pending"
            value={`${counts.escalation.shutdownPending}`}
          />
          <QueueStat
            label="Repo diminta"
            value={`${counts.escalation.repoRequested}`}
          />
        </QueueStats>
      ),
      section: () => (
        <ActionQueueSection<RentalListItem>
          columns={[mitraColumn, unitColumn, escalationColumn, arrearsColumn]}
          fetchPage={fetchPendingEscalations}
          emptyText="Tidak ada eskalasi yang menunggu action ops."
          onRowClick={openRental}
        />
      ),
    },
    refundFailed: {
      label: 'Refund Gagal',
      stats: (counts) => (
        <QueueStats>
          <QueueStat
            label="Refund tertahan"
            value={`${counts.refundFailed.count} sewa`}
            hint={`${
              counts.refundFailed.notInstructed + counts.refundFailed.failed
            } transaksi`}
          />
          <QueueStat
            label="Nilai refund"
            value={formatRupiah(counts.refundFailed.totalAmount)}
          />
          <QueueStat
            label="Belum diinstruksikan"
            value={`${counts.refundFailed.notInstructed}`}
            hint={`gagal di bank ${counts.refundFailed.failed}`}
          />
        </QueueStats>
      ),
      section: () => (
        <ActionQueueSection<RentalListItem>
          columns={[
            mitraColumn,
            unitColumn,
            {
              key: 'refund',
              title: 'Refund',
              render: (_value: unknown, record: RentalListItem) =>
                record.refund ? (
                  <div className="leading-tight">
                    <span
                      className={`inline-block rounded px-2 py-0.5 text-[12px] font-medium ${refundToneClass(
                        record.refund.payoutStatus
                      )}`}
                    >
                      {refundStatusLabel(record.refund.payoutStatus)}
                    </span>
                    {record.refund.amount ? (
                      <div className="mt-1 text-[12px] text-gray-500">
                        {formatRupiah(record.refund.amount)}
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <span className="text-gray-400">-</span>
                ),
            },
          ]}
          fetchPage={fetchStuckRefunds}
          emptyText="Tidak ada refund yang gagal."
          onRowClick={openRental}
        />
      ),
    },
  };

  return (
    <div className="mx-auto max-w-full bg-white p-6">
      <div className="mb-4">
        <h1 className="mb-1 text-[24px] font-semibold text-gray-900">
          Rent Dashboard
        </h1>
        <p className="text-[14px] text-gray-600">
          Ringkasan kondisi sewa dan antrian yang butuh action.
        </p>
      </div>

      <PerformaCards
        resource={summary}
        onRetry={reloadSummary}
        onDateRangeChange={(range) => setPickedRange(toApiDateRange(range, today))}
      />

      <FleetCards
        stats={stats}
        summary={summary}
        onRetryStats={reloadStats}
        onRetrySummary={reloadSummary}
        onOpenTab={openTab}
      />

      {/* One queue on screen at a time; switching tabs remounts the section,
          which re-fetches — an ops switching back gets fresh rows. */}
      <div className="mb-4 border-b border-gray-200">
        <nav className="flex gap-6 overflow-x-auto" aria-label="Antrian action">
          {QUEUE_ORDER.map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setActiveQueue(key)}
              className={`whitespace-nowrap border-b-2 py-3 text-[14px] font-medium ${
                activeQueue === key
                  ? 'border-primary text-gray-900'
                  : 'border-transparent text-gray-400 hover:text-gray-600'
              }`}
            >
              {queueViews[key].label}
            </button>
          ))}
        </nav>
      </div>

      {/* Three states, three renders, no fourth path. The strip takes the
          loaded counts as an argument, so there is no way to render it without
          them — which is how this screen used to paint "0 unit" on a day the
          backend was down and tell ops there was nothing to hand over. The list
          below keeps its own loading and error handling. */}
      {queues.status === 'failed' && (
        <FeedbackError
          className="mb-3"
          message="Gagal memuat hitungan antrian"
          description={queues.error}
          onRetry={reloadQueues}
        />
      )}
      {queues.status === 'loading' && (
        <QueueStats>
          <QueueStat label="Menghitung antrian" value="…" />
        </QueueStats>
      )}
      {queues.status === 'loaded' && (
        <>
          {queueViews[activeQueue].stats(queues.data.queues)}
          {/* Mounted only once the window has landed. Rendering it earlier made
              the section fetch an empty page, paint, then remount and fetch for
              real the moment the window arrived — one wasted round of state for
              I/O that never happened. */}
          {queueViews[activeQueue].section()}
        </>
      )}
    </div>
  );
};

export default RentDashboardPage;
